import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ProductOffer, ProductOffersContext } from "gql/graphql";
import { useCallbackRef } from "src/hooks/lifecycle";
import { useAppDispatch } from "src/hooks/redux";
import getStore, { AppDispatch, CoreReduxState } from "src/utils/redux/store";
import {
  CheckoutOrderEntry,
  setAddonEntries,
} from "src/utils/redux/slices/checkoutOrders";
import { getProductOffers } from "src/utils/api/purchase/productOffers";
import { createOrderRequest } from "src/utils/api/purchase/ordersCheckoutApi";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { extractBillingCycles, extractInvoice } from "src/utils/ordersUtils";
import { convertGqlOrderOperationToProto } from "src/utils/orderConversions";
import { captureException } from "src/utils/error";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { getActiveUser } from "@noom/hive-auth";
import { getRouteId } from "src/utils/meristemContext";
import { getURLParams } from "src/utils/urlParams";
import { getTrackingParams, getExperimentsInfo } from "src/api/utils";

type UseAddonOffersOptions = {
  skipInitialOrderCreation?: boolean;
  skipInitialLoad?: boolean;
};

type CreateAddonOrdersOptions = {
  previewMode?: boolean;
};

function updateEntryWithOrder(
  existing: CheckoutOrderEntry,
  order: OrderProto
): CheckoutOrderEntry {
  const isOneTimePurchase = existing.purchaseType === "ONE_TIME_PURCHASE";

  const updated: CheckoutOrderEntry = {
    ...existing,
    order,
  };

  if (isOneTimePurchase) {
    updated.invoice = extractInvoice(order);
  } else {
    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(order);
    updated.initialBillingCycle = initialBillingCycle;
    updated.recurringBillingCycle = recurringBillingCycle;
  }

  return updated;
}

function buildShallowEntry(offer: ProductOffer): CheckoutOrderEntry {
  const op = offer.orderOperation;
  const operation = convertGqlOrderOperationToProto(op);

  return {
    productName: offer.productName,
    purchaseType: offer.purchaseType,
    saleItem: {
      saleItemId: op.saleItemId,
      trialSaleItemId: op.trialSaleItemId ?? undefined,
    },
    operation,
  };
}

function buildProductOffersContext(): ProductOffersContext {
  const urlParams = getURLParams();

  return {
    routeId: getRouteId(),
    upsellAdIdentifier: urlParams.upsell_ad_id,
    experiments: getExperimentsInfo(),
    trackingParams: getTrackingParams(),
  };
}

// ── Step 1: Fetch offers and populate shallow entries ──

let activeFetchPromise: Promise<void> | null = null;

async function fetchAddonOffers(dispatch: AppDispatch): Promise<void> {
  if (activeFetchPromise) {
    return activeFetchPromise;
  }

  activeFetchPromise = (async () => {
    try {
      const context = buildProductOffersContext();
      const offers = await getProductOffers(context);

      const purchaseOffers = offers.filter(
        // eslint-disable-next-line no-underscore-dangle
        (offer) => offer.orderOperation.__typename === "PurchaseOperation"
      );

      const entries: Record<string, CheckoutOrderEntry> = {};
      for (const offer of purchaseOffers) {
        entries[offer.productName] = buildShallowEntry(offer);
      }

      dispatch(setAddonEntries(entries));
    } finally {
      activeFetchPromise = null;
    }
  })();

  return activeFetchPromise;
}

// ── Step 2: Create orders for all addon entries ──

let activeOrderCreationPromise: Promise<void> | null = null;

async function createOrdersForEntries(
  dispatch: AppDispatch,
  options: CreateAddonOrdersOptions = {}
) {
  if (activeOrderCreationPromise) {
    return activeOrderCreationPromise;
  }

  activeOrderCreationPromise = (async () => {
    try {
      const { previewMode = true } = options;
      const state = getStore().getState();
      const currentAddons = state.checkoutOrders.addons;
      if (!currentAddons) {
        throw new Error("No addon offers available for order creation");
      }

      // Preview orders are pricing-only: omit accessCode so billing does not
      // eligibility-gate the PDP batch. Real checkout orders still pass accessCode.
      const accessCode = previewMode ? undefined : getActiveUser()?.accessCode;

      // Real orders are created only for cart products: ordering an
      // already-owned offer (e.g. metabolic) fails with ALREADY_HAS_PRODUCT and
      // aborts the whole Promise.all. Preview keeps all offers for PDP pricing.
      // TODO: MGEXP-3750 — force a fresh eligible-offers fetch on the PDP
      // (currently cached) and remove this cart filter.
      const cart = state.checkoutOrders.addonsCart;
      const allEntries = Object.entries(currentAddons);
      const addonEntries =
        !previewMode && cart?.length
          ? allEntries.filter(([key]) => cart.includes(key))
          : allEntries;
      const results = await Promise.all(
        addonEntries.map(async ([key, entry]) => {
          const order = await createOrderRequest(
            entry.operation,
            accessCode,
            previewMode
          );
          return { key, entry, order };
        })
      );

      const updatedAddons: Record<string, CheckoutOrderEntry> = {
        ...currentAddons,
      };
      for (const { key, entry, order } of results) {
        updatedAddons[key] = updateEntryWithOrder(entry, order);
      }

      dispatch(setAddonEntries(updatedAddons));
    } finally {
      activeOrderCreationPromise = null;
    }
  })();

  return activeOrderCreationPromise;
}

// ── Hook ──

export function useAddonOffers(options: UseAddonOffersOptions = {}) {
  const { skipInitialOrderCreation = false, skipInitialLoad = false } = options;
  const dispatch = useAppDispatch();
  const addons = useSelector(
    (state: CoreReduxState) => state.checkoutOrders.addons
  );
  const [isLoading, setIsLoading] = useState(!addons && !skipInitialLoad);

  useEffect(() => {
    if (skipInitialLoad || addons) {
      return;
    }

    async function loadAddonOffers() {
      const task = trackTask("LoadAddonOffers");
      task.start();

      try {
        await fetchAddonOffers(dispatch);

        if (!skipInitialOrderCreation) {
          await createOrdersForEntries(dispatch, { previewMode: true });
        }

        task.success();
      } catch (ex) {
        task.failed(ex);
        captureException(ex);
      } finally {
        setIsLoading(false);
      }
    }

    loadAddonOffers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function createAddonOrders(
    createOptions: CreateAddonOrdersOptions = {}
  ) {
    const task = trackTask("CreateAddonOrders");
    task.start();
    setIsLoading(true);

    try {
      if (activeFetchPromise) {
        await activeFetchPromise;
      }
      await createOrdersForEntries(dispatch, createOptions);
      task.success();
    } catch (ex) {
      task.failed(ex);
      captureException(ex);
      throw ex;
    } finally {
      setIsLoading(false);
    }
  }

  const getAddon = useCallback(
    (productName: string): CheckoutOrderEntry | null => {
      return addons?.[productName] ?? null;
    },
    [addons]
  );

  return {
    addons,
    isLoading,
    getAddon,
    createAddonOrders: useCallbackRef(createAddonOrders),
  };
}
